import React from 'react';
import './BrandsTable.scss';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { t } from 'i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AllBrandsStatsResponse } from '../../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import { CellBox } from './components/cell-box/CellBox';
import { KeywordResponse } from '../../../../../shared/models/monitor/KeywordResponse';
import { TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import { findTagIdInKeyword } from '../../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../../shared/helpers/tags/TagsHelper';
import { Button } from 'primereact/button';
import useExport from '../../../../../shared/hooks/global/UseExport';

interface BrandsTableProps {
  allBrandsStatsResponse: AllBrandsStatsResponse[];
  keywordsMapper: Map<string, string>;
  commonKeywords: KeywordResponse[];
  tags: TagsResponse[];
  // V2 / NEW_STATS: replace the Brand Score column with the new Visibility % metric
  // (responses mentioning the brand / total responses). When false (V1), shows Brand Score.
  showVisibility?: boolean;
  // V2 / NEW_STATS: replace the Median Position column with Average Position. When false (V1), shows Median.
  showAveragePosition?: boolean;
}

export const BrandsTable: React.FC<BrandsTableProps> = ({
  allBrandsStatsResponse,
  keywordsMapper,
  commonKeywords,
  tags,
  showVisibility = false,
  showAveragePosition = false,
}) => {
  const { canExport } = useExport();

  const resolveKeyword = (keyword: string): string => {
    const tagId = commonKeywords ? findTagIdInKeyword(keyword, commonKeywords) : null;
    const keywordFromTags = tags ? getTagLabel(tags, tagId, keyword) : keyword;
    return keywordsMapper.get(keywordFromTags) || keywordFromTags;
  };

  const handleExport = () => {
    if (!canExport || allBrandsStatsResponse.length === 0) return;

    const headers = [
      t('brand'),
      showAveragePosition ? t('averagePosition') : t('medianPosition'),
      // V2 shows Brand Score as its own column, between position and Visibility.
      ...(showVisibility ? [t('brandScore')] : []),
      showVisibility ? t('visibility') : t('brandScore'),
      t('shareOfVoice'),
    ];
    const rows = allBrandsStatsResponse.map((row) => [
      `"${resolveKeyword(row.keyword).replace(/"/g, '""')}"`,
      showAveragePosition ? row.position : row.medianPosition,
      ...(showVisibility ? [row.brandScore] : []),
      showVisibility ? row.visibility ?? 0 : row.brandScore,
      row.shareOfVoice,
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'brands-ranking.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="brands-table stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('brandsRanking')}
          <CustomTooltip tooltipText="brandsRankingTooltip" addMargin={false} />
        </div>
        <Button
          id="btn-export-brands-ranking"
          className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
          onClick={handleExport}
        >
          <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
        </Button>
        <CustomTooltip
          tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'exportCsv'}
          targetRef="btn-export-brands-ranking"
        />
      </div>
      <DataTable
        stripedRows
        paginator={true}
        scrollable
        paginatorClassName="pagination"
        removableSort
        rows={5}
        value={allBrandsStatsResponse}
        tableClassName="table"
        className="table-wrapper"
        rowsPerPageOptions={[5, 10, 15]}
        currentPageReportTemplate={t('rowsNumber') + ':'}
        sortField={'shareOfVoice'}
        sortOrder={-1}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      >
        <Column
          header="#"
          headerClassName="table__header"
          body={(_rowData, { rowIndex }) => rowIndex + 1}
          style={{ width: '5px' }}
        />
        <Column
          sortable
          field="keyword"
          header={t('brand')}
          headerClassName="table__header"
          body={(rowData) => {
            const tagId = commonKeywords ? findTagIdInKeyword(rowData.keyword, commonKeywords) : null;
            const keywordFromTags = tags ? getTagLabel(tags, tagId, rowData.keyword) : rowData.keyword;
            return keywordsMapper.get(keywordFromTags) || keywordFromTags;
          }}
        />
        <Column
          sortable
          field={showAveragePosition ? 'position' : 'medianPosition'}
          header={showAveragePosition ? t('averagePosition') : t('medianPosition')}
          headerClassName="table__header table__header--center"
          bodyClassName={`table__cell--center`}
          style={{ width: '95px' }}
          body={(rowData: AllBrandsStatsResponse, column) => (
            <CellBox
              value={showAveragePosition ? rowData.position : rowData.medianPosition}
              trend={showAveragePosition ? rowData.trends.positionTrend : rowData.trends.medianTrend}
              suffix=""
              tooltipId={`median-${column.rowIndex}`}
            />
          )}
        />
        {showVisibility && (
          <Column
            sortable
            field="brandScore"
            header={t('brandScore')}
            headerClassName="table__header table__header--center"
            bodyClassName={`table__cell--center`}
            style={{ width: '95px' }}
            body={(rowData: AllBrandsStatsResponse, column) => (
              <CellBox
                value={rowData.brandScore}
                trend={rowData.trends.visibilityTrend}
                suffix="%"
                tooltipId={`brandScore-${column.rowIndex}`}
              />
            )}
          />
        )}
        <Column
          sortable
          field={showVisibility ? 'visibility' : 'brandScore'}
          header={showVisibility ? t('visibility') : t('brandScore')}
          headerClassName="table__header table__header--center"
          bodyClassName={`table__cell--center`}
          style={{ width: '95px' }}
          body={(rowData: AllBrandsStatsResponse, column) =>
            showVisibility ? (
              <CellBox
                value={rowData.visibility ?? 0}
                trend={rowData.visibilityTrend ?? 0}
                suffix="%"
                tooltipId={`visibility-${column.rowIndex}`}
              />
            ) : (
              <CellBox
                value={rowData.brandScore}
                trend={rowData.trends.visibilityTrend}
                suffix="%"
                tooltipId={`visibility-${column.rowIndex}`}
              />
            )
          }
        />
        <Column
          sortable
          field="shareOfVoice"
          header={t('shareOfVoice')}
          headerClassName="table__header table__header--center"
          bodyClassName={`table__cell--center`}
          style={{ width: '95px' }}
          body={(rowData) => `${rowData.shareOfVoice}%`}
        />
      </DataTable>
    </div>
  );
};
